/**
 * What `/twin` does (twin slice 1, plan W4 step 8; Decision 19;
 * catalogue §8).
 *
 * `/twin` stops being a destination. The Building view moves onto the
 * Overview (`/overview?view=3d`), where everyone it admits gets it with
 * the live facts beside it, and `/twin` keeps exactly one job:
 *
 *   - BARE `/twin`, for the demo role (`service_operator`), with the flag
 *     on, is the showcase: KPI tiles, horizon slider, trail and age ramp.
 *     "Bare" means no non-empty locate parameter; anything else in the
 *     query does not make it a link.
 *   - EVERYTHING ELSE redirects to `/overview?view=3d`, carrying its
 *     parameters: every other role, the demo role with a locate parameter
 *     (a link names an object, and the Overview is where objects are
 *     found), and the flag off -- the Overview then says WHY it shows the
 *     Map instead (`twin-availability.ts`), rather than the route quietly
 *     dropping the person on it.
 *
 * Mixed roles: an account holding the demo role gets the demo, whatever
 * else it holds. Nothing here gates on a write capability:
 * `service_operator` is NOT read-only (it holds `door.*` and the full
 * `lift.*` set), and the drawers it opens are the same ones, with the
 * same verbs, as on the Overview.
 *
 * WHEN THIS SHIPS. Until spa-0.5.44 `/twin` admits `foh_operator` (and
 * the other three roles) as it always has; this redirect is 0.5.44
 * content. It must ship with the Overview's Building view (W4 steps
 * 10-11), or the roles it redirects land on an Overview that cannot
 * show 3D at all.
 *
 * THE REDIRECT'S BYTES follow the locate contract (`locate.ts`): the
 * locate parameters in the order site, view, level, focus, from, each
 * escaped as `quote(value, safe=':/')`; `view` is always `3d` because
 * `/twin` means the 3D view, whatever a hand-built URL says. Any other
 * parameter is carried after them, in its own order, escaped the same
 * way, so nothing a link carried is silently dropped. It is built here
 * rather than by `locateHref` because a bare `/twin` has no site, which
 * `locateHref` (rightly) refuses.
 */
import { DEMO_ROLE, hasAnyRole, type RoleLike } from '../../auth/roles';
import {
  LOCATE_PARAM_ORDER,
  LOCATE_ROUTE,
  hasLocateParams,
  quoteLocateValue,
} from '../../state/locate';

export type TwinRouteDecision = { kind: 'demo' } | { kind: 'redirect'; to: string };

export interface TwinRouteInputs {
  readonly roles: Iterable<RoleLike> | null | undefined;
  /** The location's search string, `?` or not. */
  readonly search: string;
  /** `getRuntimeConfig().featureFlags.twinEnabled`; absent means off. */
  readonly twinEnabled: boolean | null | undefined;
}

const LOCATE_NAMES: ReadonlySet<string> = new Set<string>(LOCATE_PARAM_ORDER);

/** `/overview?…&view=3d…` for a `/twin` search string. */
export function twinRedirectHref(search: string): string {
  const params = new URLSearchParams(search);
  const parts: string[] = [];
  for (const name of LOCATE_PARAM_ORDER) {
    const value = name === 'view' ? '3d' : params.get(name);
    if (value) parts.push(`${name}=${quoteLocateValue(value)}`);
  }
  for (const [name, value] of params) {
    if (LOCATE_NAMES.has(name)) continue;
    parts.push(`${quoteLocateValue(name)}=${quoteLocateValue(value)}`);
  }
  return `${LOCATE_ROUTE}?${parts.join('&')}`;
}

/** Pure: the demo, or where to send the person instead. */
export function twinRouteDecision(inputs: TwinRouteInputs): TwinRouteDecision {
  const demo =
    inputs.twinEnabled === true &&
    hasAnyRole(inputs.roles, [DEMO_ROLE]) &&
    !hasLocateParams(inputs.search);
  return demo ? { kind: 'demo' } : { kind: 'redirect', to: twinRedirectHref(inputs.search) };
}
